import { useEffect } from 'react';
import Product from './components/product';
import TopNav from './components/navBar';
import NotFound from './components/notFound';
import Footer from './components/footer';
import Terms from './components/terms';
import Licenses from './components/licenses';
import MarketplaceRedirect from './components/marketplaceRedirect';
import HomeRedirect from './components/homeRedirect';
import EBayOAuth from './components/ebayLogin';
import EtsyOAuth from './components/etsyLogin';
import FrameTest from './components/frameTest';
import Dashboard from './components/dashboard';
import ProductsRedirect from './components/productsRedirect';
import DashboardLayout from './components/dashboardLayout';
import Settings from './components/settings';
import CustomerSelect from './components/customerSelect';
import ProductPricingTable from './components/productPricingTable';
import StripePricingTable from './components/stripePricingTable';
import StartTrial from './components/startTrial';
import Uploader from './components/uploader';
import ProductBulkEdit from './components/productBulkEdit';
import ProductListV3 from './components/products/ProductListV3';
import ProductSettingsPage from './components/products/ProductSettingsPage';
import SalesPage from './components/sales/SalesPage';
import SalesMetricsPage from './components/sales/SalesMetricsPage';
import Login from './components/login';
import CreateAccount from './components/createAccount';
import LinkShopify from './components/linkShopify';
import AuthPopup from './components/authPopup';
import Team from './components/team';
import Blog from './components/blog';
import { createBrowserRouter, RouterProvider, Navigate, useParams } from 'react-router-dom';
import ContactForm from './components/contact';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

import './shopik.webapp.default.css';
import './firebase';

// Import Auth components
import { AuthProvider } from './components/authContext'; // New centralized AuthContext
import  PrivateRoute from './components/privateRoute'; // A PrivateRoute wrapper
import UserManagement from './components/userManagement';
import ProductEditGeneric from './components/productEditGeneric';
import AppSmartBanner from './components/appSmartBanner';

const theme = createTheme({
  palette: {
    primary: { main: '#111111', contrastText: '#ffffff' },
    secondary: { main: '#111111' },
    text: { primary: '#111111' },
  },
  shape: { borderRadius: 8 },
  components: {
    MuiButton: {
      styleOverrides: {
        contained: {
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: 'uppercase',
          fontWeight: 600,
          '&.Mui-selected, &.Mui-selected:hover': {
            backgroundColor: '#111111',
            color: '#ffffff',
          },
        },
      },
    },
  },
});

function LegacyProductListRedirect() {
  const { customer } = useParams();
  return <Navigate to={`/products-v2/${customer}`} replace />;
}

// Router setup
const router = createBrowserRouter([
  {
    path: '/',
    element: <HomeRedirect/>,
    errorElement: (
      <>
        <TopNav />
        <NotFound />
        <Footer />
      </>
    ),
  },
  {
    path: 'terms',
    element: (
      <>
        <Terms />      
      </>
    ),
  },
  {
    path: 'licenses',
    element: (
      <>
        <TopNav />
        <Licenses />
        <Footer />
      </>
    ),
  },
  {
    path: 'team',
    element: (
      <>
        <TopNav />
        <Team />
        <Footer />
      </>
    ),
  },
  {
    path: 'contact',
    element: (
      <>
        <ContactForm />
      </>
    ),
  },
  {
    path: 'product-bulk-edit/:customer',
    element:  <PrivateRoute>
    <DashboardLayout>
    <ProductBulkEdit />
    </DashboardLayout>
    </PrivateRoute>,
  },
  {
    path: 'product-bulk-edit/:customer/:productUri',
    element: (
      <PrivateRoute>
        <DashboardLayout>
          <ProductBulkEdit />
        </DashboardLayout>
      </PrivateRoute>
    ),
  },
  {
    // Legacy list page (buggy pagination, ignored search) — send any old
    // bookmarked/shared links straight to the current product list.
    path: 'products/:customer',
    element: <LegacyProductListRedirect />,
  },
  {
    path: 'products-v2/:customer',
    element: <PrivateRoute>
      <DashboardLayout>
        <ProductListV3 />
      </DashboardLayout>
    </PrivateRoute>,
  },
  {
    path: 'sales/:customer',
    element: <PrivateRoute>
      <DashboardLayout>
        <SalesPage />
      </DashboardLayout>
    </PrivateRoute>,
  },
  {
    path: 'sales/:customer/metrics',
    element: <PrivateRoute>
      <DashboardLayout>
        <SalesMetricsPage />
      </DashboardLayout>
    </PrivateRoute>,
  },
  {
    path: 'product-settings/:customer',
    element: <PrivateRoute>
      <DashboardLayout>
        <ProductSettingsPage />
      </DashboardLayout>
    </PrivateRoute>,
  },
  {
    path: 'marketplace',
    element: <MarketplaceRedirect />,
  },
  {
    path: 'm',
    element: <MarketplaceRedirect />,
  },
  {
    path: 'products/:customer/:product',
    element:  <PrivateRoute>
    <DashboardLayout>
      <Product/>
    </DashboardLayout>
    </PrivateRoute>,
  },
  {
    path: 'm/:customer/:product',
    element:  <PrivateRoute>
    <DashboardLayout>
      <Product />
    </DashboardLayout>
    </PrivateRoute>,
  },
  {
    path: 'casestudies/:customer',
    element: <FrameTest />,
  },
  {
    path: 'blog/:slug',
    element: <Blog />,
  },
  {
    path: 'blog/',
    element: <Blog />,
  },
  {
    path: 'uploader',
    element: (
      <PrivateRoute>
        <DashboardLayout>
          <Uploader />
        </DashboardLayout>
      </PrivateRoute>
    ),
  },
  {
    path: 'my-products',
    element: (
      <PrivateRoute>
        <ProductsRedirect />
      </PrivateRoute>
    ),
  },
  {
    path: 'dashboard',
    element: (
      <PrivateRoute>
        <DashboardLayout>
          <Dashboard />
        </DashboardLayout>
      </PrivateRoute>
    ),
  },
  {
    path: 'user-management',
    element: (
      <PrivateRoute>
        <DashboardLayout>
          <UserManagement />
        </DashboardLayout>
      </PrivateRoute>
    ),
  },
  {
    path: 'settings',
    element: (
      <PrivateRoute>
        <DashboardLayout>
          <Settings />
        </DashboardLayout>
      </PrivateRoute>
    ),
  },{
    path: 'plans',
    element: (
      <PrivateRoute>
        <DashboardLayout>
          <StripePricingTable />
        </DashboardLayout>
      </PrivateRoute>
    ),
  },{
    path: 'start-trial',
    element: (
      <PrivateRoute>
        <DashboardLayout>
          <StartTrial />
        </DashboardLayout>
      </PrivateRoute>
    ),
  },
  {
    path: 'pricing/:customer/:document',
    element: (
      <ProductPricingTable  carPartList={true} />
    ),
  },
  {
    path: 'product-pricing/:customer/:document',
    element: (
      <ProductPricingTable  carPartList={false} />
    ),
  },
  {
    path: 'login',
    element:(
          <Login />
    ),
  },
  {
    path: 'signup',
    element:(
          <CreateAccount />
    ),
  },
  {
    path: 'link-shopify',
    element:(
          <LinkShopify />
    ),
  },
  {
    path: 'auth-popup',
    element: <AuthPopup />,
  },
  {
    path: 'customer-select',
    element:(
      <PrivateRoute>
      <CustomerSelect/>
    </PrivateRoute>
    ),
  },
  {
    path: 'ebayLogin/:customer/:destination',
    element: <EBayOAuth />,
  },
  {
    path: 'ebayLogin',
    element: <EBayOAuth />,
  },
  {
    path: 'etsy/complete',
    element: <EtsyOAuth />,
  },
]);

function App() {
  useEffect(() => {
    window.Webflow && window.Webflow.destroy();
    window.Webflow && window.Webflow.ready();
    window.Webflow && window.Webflow.require('ix2').init();
    document.dispatchEvent(new Event('readystatechange'));
  });

  useEffect(() => {
    // Define the event listener callback function
    const handleMessage = (event) => {
      if (event.data) {
        const styles = event.data;
        Object.keys(styles).forEach((variable) => {
          document.documentElement.style.setProperty(variable, styles[variable]);
        });
      }
    };
  
    // Add the event listener
    window.addEventListener('message', handleMessage);
  
    // Cleanup function to remove the event listener
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <AuthProvider>
        <div className="App">
          <AppSmartBanner />
          <RouterProvider router={router} />
        </div>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;